import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Globe, Send, Loader2, CheckCircle2 } from "lucide-react";
import SectionHead from "../SectionHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO_NEEDED = [
    "Component drawing",
    "Material specification / grade",
    "Dimensions & tolerances",
    "Required quantity",
    "Application details",
    "Operating temperature",
    "Electrical requirements",
    "Required tests / standards",
    "Surface finish requirements",
    "Packaging requirements",
];

const COMPONENT_TYPES = [
    "FRP Epoxy Box / Enclosure",
    "FRP / GFRP Sheets",
    "FRP Rods, Profiles & Strips",
    "FRP Washers & Rings",
    "Custom Machined Component",
    "Transformer Insulation Component",
    "Other / Multiple",
];

const EMPTY = {
    name: "",
    company: "",
    phone: "",
    email: "",
    component_type: "",
    quantity: "",
    application: "",
    message: "",
    website_url: "",
};

const Field = ({ label, required, children, testid }) => (
    <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            {label} {required && <span className="text-safety">*</span>}
        </label>
        <div data-testid={testid}>{children}</div>
    </div>
);

export const Enquiry = () => {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            const res = await axios.post(`${API}/enquiry`, form, { timeout: 25000 });
            if (res.data?.status === "success") {
                toast.success("Enquiry received. Our technical team will respond shortly.", {
                    description: "For urgent requirements, call +91 9648881601.",
                });
                setForm(EMPTY);
            } else {
                throw new Error("unexpected response");
            }
        } catch (err) {
            toast.error("Could not send right now — please call +91 9648881601 instead.", {
                description: "You can also email naagarjunpowerfiber@gmail.com.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="enquiry" className="py-24 md:py-32 scroll-mt-20 bg-panel/40 border-t border-zinc-900">
            <div className="shell">
                <SectionHead
                    num="12"
                    overline="Enquiry & Contact"
                    title="Request a technical evaluation"
                    desc="Share your requirement — upon receiving these details, we can evaluate tooling, manufacturing process, capacity, lead time and commercial feasibility."
                />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    <div className="lg:col-span-5 space-y-8">
                        <div className="border border-zinc-800 bg-[#0e0e11] p-6 md:p-8">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-safety mb-5">
                                For evaluation, please share
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                                {INFO_NEEDED.map((item, i) => (
                                    <div
                                        key={item}
                                        data-testid={`enquiry-info-needed-${i + 1}`}
                                        className="flex items-start gap-2.5 text-sm text-zinc-300"
                                    >
                                        <CheckCircle2 size={14} className="text-safety mt-0.5 shrink-0" />
                                        {item}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="border border-zinc-800 bg-[#0e0e11] p-6 md:p-8 space-y-5">
                            <div className="flex items-start gap-4" data-testid="contact-address">
                                <MapPin size={18} className="text-safety mt-1 shrink-0" />
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                                        Address
                                    </div>
                                    <p className="text-sm text-zinc-200 leading-relaxed">
                                        Plot No. G-27, Road No. 10,<br />
                                        SIDA, Satharia, Jaunpur,<br />
                                        Uttar Pradesh – 222202, India
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4" data-testid="contact-phones">
                                <Phone size={18} className="text-safety mt-1 shrink-0" />
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                                        Mobile
                                    </div>
                                    <a
                                        href="tel:+919648881601"
                                        data-testid="contact-phone-1"
                                        className="block text-sm text-zinc-200 hover:text-safety transition-colors"
                                    >
                                        +91 9648881601
                                    </a>
                                    <a
                                        href="tel:+919794946308"
                                        data-testid="contact-phone-2"
                                        className="block text-sm text-zinc-200 hover:text-safety transition-colors"
                                    >
                                        +91 9794946308
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4" data-testid="contact-email-block">
                                <Mail size={18} className="text-safety mt-1 shrink-0" />
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                                        Email
                                    </div>
                                    <a
                                        href="mailto:naagarjunpowerfiber@gmail.com"
                                        data-testid="contact-email-link"
                                        className="text-sm text-zinc-200 hover:text-safety transition-colors break-all"
                                    >
                                        naagarjunpowerfiber@gmail.com
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4" data-testid="contact-website-block">
                                <Globe size={18} className="text-safety mt-1 shrink-0" />
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                                        Website
                                    </div>
                                    <span className="text-sm text-zinc-200">naagarjunpowerfiber.in</span>
                                </div>
                            </div>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="border border-zinc-800 bg-[#0e0e11] overflow-hidden"
                            data-testid="contact-map-card"
                        >
                            <div className="px-6 pt-5 pb-4 flex items-center gap-3">
                                <MapPin size={16} className="text-safety" />
                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                                    Find us — SIDA Industrial Area, Jaunpur
                                </span>
                            </div>
                            <iframe
                                title="Naagarjun Power Fiber location — SIDA, Satharia, Jaunpur"
                                src="https://www.google.com/maps?q=Satharia%20Industrial%20Area%2C%20SIDA%2C%20Jaunpur%2C%20Uttar%20Pradesh%20222202%2C%20India&output=embed"
                                className="w-full h-[260px] block"
                                style={{ border: 0, filter: "grayscale(0.35) contrast(1.05)" }}
                                loading="lazy"
                                allowFullScreen
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="contact-map-embed"
                            />
                        </motion.div>
                    </div>

                    <div className="lg:col-span-7">
                        <form
                            onSubmit={submit}
                            data-testid="enquiry-form"
                            className="border border-zinc-800 bg-[#0e0e11] p-6 md:p-10"
                        >
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label="Your Name" required testid="enquiry-name-field">
                                    <input
                                        className="field"
                                        required
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Full name"
                                        data-testid="enquiry-name-input"
                                    />
                                </Field>
                                <Field label="Company" testid="enquiry-company-field">
                                    <input
                                        className="field"
                                        value={form.company}
                                        onChange={set("company")}
                                        placeholder="Company / OEM name"
                                        data-testid="enquiry-company-input"
                                    />
                                </Field>
                                <Field label="Phone" required testid="enquiry-phone-field">
                                    <input
                                        className="field"
                                        required
                                        type="tel"
                                        value={form.phone}
                                        onChange={set("phone")}
                                        placeholder="+91 ..."
                                        data-testid="enquiry-phone-input"
                                    />
                                </Field>
                                <Field label="Email" testid="enquiry-email-field">
                                    <input
                                        className="field"
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="you@company.com"
                                        data-testid="enquiry-email-input"
                                    />
                                </Field>
                                <Field label="Component Type" required testid="enquiry-type-field">
                                    <select
                                        className="field"
                                        required
                                        value={form.component_type}
                                        onChange={set("component_type")}
                                        data-testid="enquiry-type-select"
                                    >
                                        <option value="" disabled>
                                            Select component
                                        </option>
                                        {COMPONENT_TYPES.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Required Quantity" testid="enquiry-quantity-field">
                                    <input
                                        className="field"
                                        value={form.quantity}
                                        onChange={set("quantity")}
                                        placeholder="e.g. 50 pcs / month"
                                        data-testid="enquiry-quantity-input"
                                    />
                                </Field>
                            </div>

                            <div className="mt-5">
                                <Field label="Application Details" testid="enquiry-application-field">
                                    <textarea
                                        className="field resize-none"
                                        rows={3}
                                        value={form.application}
                                        onChange={set("application")}
                                        placeholder="Application, operating temperature, electrical requirements, required tests / standards…"
                                        data-testid="enquiry-application-input"
                                    />
                                </Field>
                            </div>

                            <div className="mt-5">
                                <Field label="Message" testid="enquiry-message-field">
                                    <textarea
                                        className="field resize-none"
                                        rows={3}
                                        value={form.message}
                                        onChange={set("message")}
                                        placeholder="Anything else — drawings available, target date, packaging needs…"
                                        data-testid="enquiry-message-input"
                                    />
                                </Field>
                            </div>

                            {/* honeypot — invisible to humans */}
                            <input
                                type="text"
                                name="website_url"
                                value={form.website_url}
                                onChange={set("website_url")}
                                className="hidden"
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                            />

                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="enquiry-form-submit"
                                className="btn-solid w-full sm:w-auto mt-8 justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {sending ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" /> Sending…
                                    </>
                                ) : (
                                    <>
                                        Submit Enquiry <Send size={15} />
                                    </>
                                )}
                            </button>
                            <p className="mt-4 text-xs text-zinc-500">
                                Your enquiry goes straight to our technical team at
                                naagarjunpowerfiber@gmail.com.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Enquiry;